import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import plus from "../assets/plus.svg";
import edit from "../assets/edit.svg";
import deleteIcon from "../assets/delete.svg";
import NewTestForm from "../components/NewTestForm/NewTestForm";
import TestChartEntry from "../components/TestChartEntry/TestChartEntry";
import Spinner from "../components/Spinner/Spinner";
import OrganSystemView from "../components/OrganSystemView";
import UserHeader from "../components/UserHeader/UserHeader";
import TestDeletePopup from "../components/TestDeletePopup";
import DesktopProtocol from "../components/Recommendations/DesktopProtocol";
import ExportButton from "../components/Export/ExportButton";
import ShareModal from "../components/ShareModal/ShareModal";
import ChatDrawer from "../components/ChatDrawer/ChatDrawer";
import ElisLockedPanel from "../components/ChatDrawer/ElisLockedPanel";
import FocusDashboard from "../components/FocusDashboard/FocusDashboard";
import { useScreen } from "../context/ScreenProvider";
import { formatDate } from "../util/GenericUtil";
import { formatDrawLabel } from "../util/DrawGroupUtil";
import bloodGroupings from "../../lambda/health-recommendations/blood_groupings.json";
import { OrganIcon } from "../components/OrganSystemCircle/OrganIcons";
import "../styles/buttons.css";
import "../styles/Home.css";

function DesktopHome({
    user,
    profileData,
    demo = false,
    demoMode = false,
    defaultChatOpen = false,
    doctorViewMode,
    readOnly,
    patientId,
    patientName,
    bloodTestFiles,
    loadingFiles,
    uploadingState,
    setUploadingState,
    refreshUserFiles,
    handleFileInput,
    mergeDraw,
    userFiles,
    setUserFiles,
    effectiveSelectedFile,
    selectedFileIndex,
    filteredTests,
    testsForExport,
    editTests,
    newTest,
    setNewTest,
    addEditTest,
    updateEditTest,
    createNewTest,
    normalizeTestName,
    getFxRange,
    sex,
    recommendations,
    recommendationMode,
    setRecommendationMode,
    loadingRecommendations,
    selectedAlternatives,
    handleSelectAlternative,
    handleGetRecommendations,
    handleGetAllDefaults,
    highlightedRecommendations,
    savedProtocol,
    protocolLocked,
    effectiveLocked,
    hasActiveDoctor,
    lockedByName,
    lockLoading,
    saveProtocol,
    toggleLock,
    canRegenerate,
    systemScoresArray,
    isLoadingScores,
    previousScoresMap,
    selectedTest,
    handleClearSelectedTest,
    handleTestClick,
    selectedSystem,
    selectedFile,
    tags,
    selectSystem,
    selectFile,
    setTags,
    clearFilters,
    activeTab,
    setActiveTab,
    showElisTab,
    elisLocked,
    elisTrialUsed,
    assessmentSummary,
    prevAssessmentSummary,
    userFocuses,
    tabPortalRef,
    exportPortalRef,
}) {
    const { isTablet } = useScreen();

    const greeting = (() => {
        const hours = new Date().getHours();
        const timeGreeting = hours < 12 ? "Good morning" : hours < 17 ? "Good afternoon" : "Good evening";
        const name = (profileData?.givenName || profileData?.familyName)
            ? [profileData.givenName, profileData.familyName].filter(Boolean).join(' ')
            : (user?.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : "");
        return `${timeGreeting}, ${name}`;
    })();

    const assessmentFiles = useMemo(() => {
        if (!userFiles) return [];
        return userFiles.filter(f => f.key.includes('assessments/summaries/'));
    }, [userFiles]);

    const exportRecommendations = useMemo(() => {
        const entries = savedProtocol?.entries;
        if (!entries?.length) return [];
        return entries.map(entry => ({
            product_details: {
                product_name: entry.name,
                min_daily_dose: entry.dose,
                unit: entry.unit,
                freq_unit: entry.freqUnit,
                empty_stomach: entry.emptyStomach,
            },
            fulfills_categories: [{ category: entry.category, weight: 0 }],
            total_weight: 0,
            category: entry.category,
            doseOverride: {
                dose: entry.dose,
                frequency: entry.frequency,
                dailyTotal: entry.dailyTotal,
                emptyStomach: entry.emptyStomach,
                instructions: entry.instructions || null,
            },
        }));
    }, [savedProtocol]);

    const [addTestPopover, setAddTestPopover] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [deletePopup, setDeletePopup] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [sharePreSelectedFile, setSharePreSelectedFile] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [panelCollapsed, setPanelCollapsed] = useState(false);
    const [desktopFlatMode, setDesktopFlatMode] = useState(false);
    const [activePanelSystem, setActivePanelSystem] = useState(null);
    const [chatOpen, setChatOpen] = useState(defaultChatOpen);
    const [chatFullscreen, setChatFullscreen] = useState(false);
    const [chatWidth, setChatWidth] = useState(400);
    // Demo: pulse the Protocol tab until it's first opened.
    const [protocolPulse, setProtocolPulse] = useState(demo);
    const [isResizingState, setIsResizingState] = useState(false);
    const isResizing = useRef(false);
    const addTestMenuRef = useRef(null);
    const systemRefs = useRef({});

    const CHAT_MIN = 340;
    const CONTENT_MIN = 1150;

    const availableForChat = window.innerWidth - CONTENT_MIN;
    const chatOverlapping = chatOpen && chatWidth > availableForChat;
    const overlapAmount = chatOverlapping ? chatWidth - availableForChat : 0;

    const handleResizeStart = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        setIsResizingState(true);
        const startX = e.clientX;
        const startW = chatWidth;
        const maxW = window.innerWidth - 200;

        const onMove = (ev) => {
            if (!isResizing.current) return;
            const delta = startX - ev.clientX;
            setChatWidth(Math.min(maxW, Math.max(CHAT_MIN, startW + delta)));
        };
        const onUp = () => {
            isResizing.current = false;
            setIsResizingState(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [chatWidth]);

    // Demo: stop the Protocol-tab pulse once the user opens the Protocol tab.
    useEffect(() => {
        if (activeTab === 'protocol') setProtocolPulse(false);
    }, [activeTab]);

    useEffect(() => {
        const handleClick = (e) => {
            if (addTestMenuRef.current && !addTestMenuRef.current.contains(e.target)) {
                setAddTestPopover(false);
            }
        };
        window.addEventListener("mousedown", handleClick);
        return () => window.removeEventListener("mousedown", handleClick);
    }, []);

    const searchFilteredTests = useMemo(() => {
        if (!searchQuery.trim()) return filteredTests;
        const q = searchQuery.trim().toLowerCase();
        return filteredTests.filter(test => {
            const name = (test.alias || test.test_name || '').toLowerCase();
            return name.includes(q);
        });
    }, [filteredTests, searchQuery]);

    const systemGroupedTests = useMemo(() => {
        if (!searchFilteredTests.length) return {};
        const groups = {};
        const systemKeys = selectedSystem
            ? [selectedSystem]
            : Object.keys(bloodGroupings);

        const testsByName = {};
        searchFilteredTests.forEach(test => {
            const testName = test.alias || test.test_name;
            if (!testsByName[testName]) testsByName[testName] = test;
        });

        const placed = new Set();

        systemKeys.forEach(systemKey => {
            const system = bloodGroupings[systemKey];
            if (!system) return;
            const systemMarkers = Object.keys(system.markers);
            const orderedTests = systemMarkers
                .filter(marker => marker in testsByName)
                .map(marker => {
                    placed.add(marker);
                    return testsByName[marker];
                });
            if (orderedTests.length > 0) {
                groups[systemKey] = orderedTests;
            }
        });

        if (!selectedSystem) {
            const other = searchFilteredTests.filter(test => {
                const testName = test.alias || test.test_name;
                return !placed.has(testName);
            });
            if (other.length > 0) {
                groups._other = other;
            }
        }

        return groups;
    }, [searchFilteredTests, selectedSystem]);

    const letterGroupedTests = useMemo(() => {
        if (!desktopFlatMode || !searchFilteredTests.length) return {};
        const sorted = [...searchFilteredTests].sort((a, b) => {
            const nameA = (a.alias || a.test_name || '').toLowerCase();
            const nameB = (b.alias || b.test_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
        const groups = {};
        sorted.forEach(test => {
            const name = (test.alias || test.test_name || '').toUpperCase();
            const letter = name[0] || '#';
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(test);
        });
        return groups;
    }, [searchFilteredTests, desktopFlatMode]);

    const availableSystemKeys = useMemo(() => {
        if (!bloodTestFiles?.length) return new Set();
        const testsByName = {};
        bloodTestFiles.forEach(file => {
            (file.data?.body || []).forEach(test => {
                testsByName[test.alias || test.test_name] = true;
            });
        });
        return new Set(
            Object.keys(bloodGroupings).filter(key => {
                const markers = Object.keys(bloodGroupings[key].markers);
                return markers.some(m => m in testsByName);
            })
        );
    }, [bloodTestFiles]);

    const panelSystems = useMemo(() => {
        if (desktopFlatMode) {
            const letters = Object.keys(letterGroupedTests);
            return [
                { key: '_sys', icon: 'sys', label: 'Organ Systems' },
                ...letters.map(l => ({ key: `letter_${l}`, icon: l, label: l })),
            ];
        }
        const systems = Object.keys(bloodGroupings)
            .filter(key => availableSystemKeys.has(key))
            .map(key => ({ key, icon: bloodGroupings[key].icon, label: bloodGroupings[key].display_name }));
        const result = [{ key: 'all', icon: 'all', label: 'Alphabetical' }, ...systems];
        const allMarkers = new Set(
            Object.values(bloodGroupings).flatMap(sys => Object.keys(sys.markers))
        );
        const hasOther = (bloodTestFiles || []).some(file =>
            (file.data?.body || []).some(test => !allMarkers.has(test.alias || test.test_name))
        );
        if (hasOther) {
            result.push({ key: '_other', icon: 'other', label: 'Other' });
        }
        return result;
    }, [availableSystemKeys, bloodTestFiles, letterGroupedTests, desktopFlatMode]);

    const handlePanelSelect = (key) => {
        if (key === '_sys') {
            setDesktopFlatMode(false);
            setActivePanelSystem(null);
            return;
        }
        if (key === 'all') {
            setDesktopFlatMode(true);
            setActivePanelSystem('all');
            return;
        }
        if (selectedSystem && selectedSystem !== key) {
            selectSystem(null);
        }
        setTimeout(() => {
            requestAnimationFrame(() => {
                const el = systemRefs.current[key];
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    setActivePanelSystem(key);
                }
            });
        }, 0);
    };

    const handlePanelDoubleClick = (key) => {
        if (key === '_sys' || key === 'all' || key.startsWith('letter_')) return;
        selectSystem(selectedSystem === key ? null : key);
        setActivePanelSystem(key);
    };

    const renderOrganGroupedTests = (aggregate) => {
        const groups = desktopFlatMode ? letterGroupedTests : systemGroupedTests;
        const entries = desktopFlatMode
            ? Object.entries(groups).map(([letter, tests]) => [`letter_${letter}`, tests, letter])
            : Object.entries(groups).map(([key, tests]) => [key, tests, null]);

        return entries.map(([systemKey, tests, letter]) => {
            const isOther = systemKey === '_other';
            const isLetter = systemKey.startsWith('letter_');
            const system = (isOther || isLetter) ? null : bloodGroupings[systemKey];
            return (
                <div key={systemKey} className="organ-system-group" data-system={systemKey}
                     ref={el => systemRefs.current[systemKey] = el}>
                    <div className="organ-system-group-header">
                        {isLetter ? (
                            <span>{letter}</span>
                        ) : isOther ? (
                            <span>Other</span>
                        ) : (
                            <>
                                <OrganIcon icon={system.icon} />
                                <span>{system.display_name}</span>
                            </>
                        )}
                    </div>
                    {tests.map((test, i) => (
                        <TestChartEntry
                            aggregate={aggregate}
                            key={`${systemKey}-${i}-${effectiveSelectedFile}`}
                            test={test}
                            tags={tags}
                            onTestClick={aggregate ? () => handleTestClick(test, systemKey) : undefined}
                            isSelected={aggregate ? selectedTest?.test_name === test.test_name && selectedTest?._groupKey === systemKey : false}
                        />
                    ))}
                </div>
            );
        });
    };

    const getFilteredTests = () => {
        if (searchFilteredTests.length === 0 && (selectedSystem || searchQuery.trim())) {
            const systemName = bloodGroupings[selectedSystem]?.display_name || selectedSystem;
            const testDate = typeof effectiveSelectedFile === 'number' && bloodTestFiles[effectiveSelectedFile]
                ? formatDate(bloodTestFiles[effectiveSelectedFile].data.header.testdate?.toString())
                : 'this selection';
            return (
                <div className="emptyMarkersMessage">
                    There are no markers specific to {systemName} for {testDate}
                </div>
            );
        }

        if (typeof effectiveSelectedFile === 'number') {
            if (editTests && bloodTestFiles[effectiveSelectedFile] && bloodTestFiles[effectiveSelectedFile].key in editTests) {
                return (
                    <NewTestForm
                        setSelectedFile={selectFile}
                        normalizeTestName={normalizeTestName}
                        newTest={editTests[bloodTestFiles[effectiveSelectedFile].key]}
                        setNewTest={updateEditTest}
                        edit={true}
                        userFiles={userFiles}
                        setUserFiles={setUserFiles}
                        setUploadingState={setUploadingState}
                        patientId={patientId}
                        userId={user?.userId}
                    />
                );
            } else {
                return renderOrganGroupedTests(false);
            }
        } else if (effectiveSelectedFile === "all") {
            return renderOrganGroupedTests(true);
        } else if (effectiveSelectedFile === "new") {
            return (
                <NewTestForm
                    setSelectedFile={selectFile}
                    normalizeTestName={normalizeTestName}
                    newTest={newTest}
                    setNewTest={setNewTest}
                    edit={false}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setUploadingState={setUploadingState}
                    patientId={patientId}
                    userId={user?.userId}
                />
            );
        }
    };

    const renderAddTestButton = (isTabletLayout = false, showLabel = false) => (
        <div className="addTestContainer" ref={addTestMenuRef} style={showLabel ? { width: 'auto' } : undefined}>
            <button className="addTest" onClick={() => {
                setAddTestPopover(!addTestPopover)
                }} disabled={uploadingState || demo}
                title={demo ? 'Demo — not available' : undefined}
                style={showLabel ? { width: 'auto', borderRadius: '20px', padding: '8px 16px' } : undefined}>
                {uploadingState ? (
                    <div style={{ marginLeft: '-8px', display: 'flex' }}>
                        <Spinner size={20} color="#666" />
                    </div>
                ) : showLabel ? (
                    "Add Test"
                ) : (
                    <img src={plus} />
                )}
            </button>
            {addTestPopover ?
            <div className={`addTestPopover${isTabletLayout ? ' tablet' : ''}`}>
                <button onClick={handleFileInput}>Upload Test</button>
                <button onClick={() => {
                    if(!newTest) {
                        createNewTest()
                    } else {
                        alert("Please save or delete your existing test before creating a new one")
                    }
                    setAddTestPopover(false)
                }}>Manual Entry</button>
            </div>
            : ""}
        </div>
    );

    const tabBar = (
        <div className="desktop-tabs">
            <button
                className={`desktop-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
            >
                Dashboard
            </button>
            <button
                className={`desktop-tab ${activeTab === 'protocol' ? 'active' : ''}${protocolPulse ? ' demo-pulse' : ''}`}
                onClick={() => setActiveTab('protocol')}
            >
                Protocol
            </button>
            {showElisTab && (
                <button
                    className={`desktop-tab elis-tab ${chatOpen ? 'active' : ''}`}
                    onClick={() => setChatOpen(prev => !prev)}
                    title={chatOpen ? 'Close Elis' : 'Open Elis'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                    Elis
                </button>
            )}
        </div>
    );

    return (
        <div className="homeTemp">
            <div className="content">
                {!readOnly && !doctorViewMode && (
                    <UserHeader
                        demoMode={demoMode}
                        tabControl={tabBar}
                        exportButton={
                            <ExportButton
                                testDate={typeof effectiveSelectedFile === 'number' ? bloodTestFiles[effectiveSelectedFile]?.data?.header?.testdate : null}
                                systemScores={systemScoresArray}
                                tests={testsForExport}
                                recommendations={exportRecommendations}
                                recommendationMode={recommendationMode}
                                getFxRange={getFxRange}
                                focuses={userFocuses}
                                assessmentSummary={assessmentSummary}
                                sex={sex}
                                variant="dropdown"
                                disabled={!bloodTestFiles || bloodTestFiles.length === 0}
                            />
                        }
                    />
                )}
                {doctorViewMode && tabPortalRef?.current && createPortal(tabBar, tabPortalRef.current)}
                {doctorViewMode && !tabPortalRef?.current && (
                    <div className="doctor-tab-bar">{tabBar}</div>
                )}
                {doctorViewMode && exportPortalRef && createPortal(
                    <ExportButton
                        testDate={typeof effectiveSelectedFile === 'number' ? bloodTestFiles[effectiveSelectedFile]?.data?.header?.testdate : null}
                        systemScores={systemScoresArray}
                        tests={testsForExport}
                        recommendations={exportRecommendations}
                        recommendationMode={recommendationMode}
                        getFxRange={getFxRange}
                        focuses={userFocuses}
                        assessmentSummary={assessmentSummary}
                        sex={sex}
                        variant="dropdown"
                        disabled={!bloodTestFiles || bloodTestFiles.length === 0}
                    />,
                    exportPortalRef
                )}
                <div className={`contentBody with-chat ${chatOpen ? 'chat-open' : ''}`}>
                    <main className="content-main">
                        {activeTab === 'dashboard' && (
                        <div className="visualizer">
                            <div className="dashboard-section">
                                <p className="dashboard-greeting">{greeting}</p>
                                <div className="osv-container">
                                    <div className="osv-card">
                                        <OrganSystemView
                                            systemScoresArray={systemScoresArray}
                                            isLoadingScores={isLoadingScores}
                                            bloodTestFiles={bloodTestFiles}
                                            getFxRange={getFxRange}
                                            sex={sex}
                                            selectedFileIndex={selectedFileIndex}
                                            previousScores={previousScoresMap}
                                            onUploadClick={handleFileInput}
                                            onManualEntry={() => {
                                                if (!newTest) {
                                                    createNewTest();
                                                } else {
                                                    alert("Please save or delete your existing test before creating a new one");
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <FocusDashboard focuses={userFocuses} summary={assessmentSummary} prevSummary={prevAssessmentSummary} sex={sex} demo={demo} />
                            </div>
                            <div className="tabs">
                                <button
                                    className="btn-icon refreshButton"
                                    onClick={async () => {
                                        await refreshUserFiles(true);
                                    }}
                                    disabled={loadingFiles}
                                    title="Refresh files from server"
                                >
                                    {loadingFiles ? (
                                        <div style={{ marginLeft: '-8px', display: 'flex' }}>
                                            <Spinner size={20} color="#666" />
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: '20px' }}>↻</span>
                                    )}
                                </button>
                                {bloodTestFiles && bloodTestFiles.length > 1 && <div className={`btn-pill tabEntry ${effectiveSelectedFile == "all" ? "selected": ""}`} onClick={() => {selectFile("all")}}>All Tests</div>}
                                {bloodTestFiles && bloodTestFiles.length > 0 && bloodTestFiles.map((ele, i) => <div key={i} onClick={() => {selectFile(i)}} className={`btn-pill tabEntry ${effectiveSelectedFile == i ? "selected" : ""}`}>{formatDrawLabel(ele, formatDate)}</div>)}
                                {newTest && editMode == false ? <div className={`btn-pill tabEntry newTest`} onClick={() => {selectFile("new"); clearFilters();}}>New Test</div> : ""}
                                {!readOnly && !isTablet ? renderAddTestButton(false, !bloodTestFiles?.length) : ""}
                            </div>
                           {!readOnly && isTablet ? renderAddTestButton(true, !bloodTestFiles?.length) : ""}
                            {effectiveSelectedFile != "new" && bloodTestFiles?.length > 0 ?
                            <div className="test_controls">
                                <div className="tags">
                                    <div className="desktop-search-wrapper">
                                        <svg className="desktop-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="11" cy="11" r="8" />
                                            <path d="M21 21l-4.35-4.35" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="Search biomarkers..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            className="desktop-search-input"
                                        />
                                        {searchQuery && (
                                            <button className="desktop-search-clear" onClick={() => setSearchQuery('')}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
                                                    <path d="M18 6L6 18M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    {(tags.length || selectedSystem || searchQuery.trim()) ?
                                        <button className="clear" onClick={() => {
                                            clearFilters();
                                            setSearchQuery('');
                                        }}>
                                            Clear Filters
                                        </button>
                                    : ""}
                                    <select
                                        value={selectedSystem || ""}
                                        onChange={(e) => {
                                            selectSystem(e.target.value || null);
                                        }}
                                        className="categoryFilter"
                                    >
                                        <option value="">All Systems</option>
                                        {Object.entries(bloodGroupings).map(([key, system]) => (
                                            <option key={key} value={key}>{system.display_name}</option>
                                        ))}
                                    </select>
                                    <button
                                        className={`status ${tags.includes("optimal") ? "" : "inactiveTag"}`}
                                        onClick={() => setTags(tags.includes("optimal") ? tags.filter(e => e !== "optimal") : [...tags, "optimal"])}>
                                            Optimal
                                    </button>
                                    <button
                                        className={`elevated ${tags.includes("elevated") || tags.includes("diminished") ? "" : "inactiveTag"}`}
                                        onClick={() => {
                                        const newTags = new Set(tags);
                                        if (newTags.has("elevated") || newTags.has("diminished")) {
                                            newTags.delete("elevated");
                                            newTags.delete("diminished");
                                        } else {
                                            newTags.add("elevated");
                                            newTags.add("diminished");
                                        }
                                        setTags([...newTags]);}}>
                                            Dysfunctional
                                    </button>
                                    <button
                                        className={`high ${tags.includes("high") || tags.includes("low") ? "" : "inactiveTag"}`}
                                        onClick={() => {
                                        const newTags = new Set(tags);
                                        if (newTags.has("high") || newTags.has("low")) {
                                            newTags.delete("high");
                                            newTags.delete("low");
                                        } else {
                                            newTags.add("high");
                                            newTags.add("low");
                                        }
                                        setTags([...newTags]);}}>
                                            Abnormal
                                    </button>
                                </div>
                                {effectiveSelectedFile != "all" && typeof effectiveSelectedFile === 'number' && bloodTestFiles[effectiveSelectedFile] && !readOnly && !(editTests && bloodTestFiles[effectiveSelectedFile].key in editTests) ?
                                    <div className="edits">
                                        {!doctorViewMode && (
                                            <button className="edit" disabled={demo} title={demo ? "Demo — not available" : "Share this file"} onClick={() => {
                                                setSharePreSelectedFile(effectiveSelectedFile);
                                                setShareModalOpen(true);
                                            }} style={{ fontSize: '14px', padding: '4px 8px' }}>Share</button>
                                        )}
                                        {/* Editing writes back to a single S3 object. A draw assembled from
                                            several panel sheets has no single object to write to, so editing is
                                            withheld until the sheets are merged server-side. */}
                                        {(bloodTestFiles[effectiveSelectedFile].sourceKeys?.length ?? 1) <= 1 && (
                                        <button className="edit" disabled={demo} title={demo ? "Demo — not available" : "Edit test"} onClick={() => {
                                            addEditTest()
                                            }}><img src={edit} /></button>
                                        )}
                                        <button className="delete" disabled={demo} title={demo ? "Demo — not available" : "Delete test"} onClick={() => {
                                            setDeletePopup(!deletePopup)
                                        }}><img src={deleteIcon} /></button>
                                    </div>
                                : ""}
                            </div>
                            : ""}
                            <div className="testDisplayWrapper">
                                {bloodTestFiles?.length > 0 && effectiveSelectedFile !== 'new' && (
                                    <div className={`desktop-system-panel ${panelCollapsed ? 'collapsed' : ''}`}>
                                        <button
                                            className="panel-collapse-btn"
                                            onClick={() => setPanelCollapsed(!panelCollapsed)}
                                            title={panelCollapsed ? 'Expand panel' : 'Collapse panel'}
                                        >
                                            {panelCollapsed ? '›' : '‹'}
                                        </button>
                                        <div className="panel-items">
                                            {panelSystems.map(({ key, icon, label }) => (
                                                <button
                                                    key={key}
                                                    className={`panel-item ${activePanelSystem === key ? 'active' : ''} ${selectedSystem && selectedSystem !== key && key !== 'all' && key !== '_sys' ? 'dimmed' : ''}`}
                                                    onClick={() => handlePanelSelect(key)}
                                                    onDoubleClick={() => handlePanelDoubleClick(key)}
                                                    title={label}
                                                >
                                                    {key === 'all' ? (
                                                        <span className="panel-icon-label">ABC</span>
                                                    ) : key === '_sys' ? (
                                                        <span className="panel-icon-label">SYS</span>
                                                    ) : key === '_other' ? (
                                                        <span className="panel-icon-label">O</span>
                                                    ) : key.startsWith('letter_') ? (
                                                        panelCollapsed && <span className="panel-icon-label">{key.slice(7)}</span>
                                                    ) : (
                                                        <OrganIcon icon={icon} />
                                                    )}
                                                    {!panelCollapsed && <span className="panel-label">{label}</span>}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="testDisplay">
                                    {bloodTestFiles ? getFilteredTests() : ""}
                                </div>
                            </div>
                        </div>
                        )}
                        {activeTab === 'protocol' && (
                            <DesktopProtocol
                                recommendations={recommendations}
                                recommendationMode={recommendationMode}
                                setRecommendationMode={setRecommendationMode}
                                loadingRecommendations={loadingRecommendations}
                                filteredTests={filteredTests}
                                selectedAlternatives={selectedAlternatives}
                                handleSelectAlternative={handleSelectAlternative}
                                handleGetRecommendations={handleGetRecommendations}
                                handleGetAllDefaults={handleGetAllDefaults}
                                highlightedRecommendations={highlightedRecommendations}
                                bloodTestFiles={bloodTestFiles}
                                selectedFile={selectedFile}
                                doctorViewMode={doctorViewMode}
                                demo={demo}
                                patientId={patientId}
                                userId={user?.userId}
                                savedProtocol={savedProtocol}
                                protocolLocked={protocolLocked}
                                effectiveLocked={effectiveLocked}
                                hasActiveDoctor={hasActiveDoctor}
                                lockedByName={lockedByName}
                                lockLoading={lockLoading}
                                saveProtocol={saveProtocol}
                                toggleLock={toggleLock}
                                canRegenerate={canRegenerate}
                                refreshUserFiles={refreshUserFiles}
                                loadingFiles={loadingFiles}
                            />
                        )}
                    </main>
                    {showElisTab && chatOpen && (
                        <aside
                            className={`elis-panel open ${chatFullscreen ? 'fullscreen' : ''} ${chatOverlapping && !chatFullscreen ? 'overlapping' : ''} ${isResizingState ? 'resizing' : ''}`}
                            style={chatFullscreen ? undefined : { width: chatWidth, minWidth: CHAT_MIN, marginLeft: chatOverlapping ? -overlapAmount : undefined }}
                        >
                            {!chatFullscreen && <div className="elis-resize-handle" onMouseDown={handleResizeStart} />}
                            {elisLocked ? (
                                <ElisLockedPanel
                                    trialUsed={elisTrialUsed}
                                    onClose={() => { setChatOpen(false); setChatFullscreen(false); }}
                                />
                            ) : (
                                <ChatDrawer
                                    patientId={patientId}
                                    patientName={patientName}
                                    isOpen={true}
                                    onToggle={() => { setChatOpen(false); setChatFullscreen(false); }}
                                    fullscreen={chatFullscreen}
                                    onFullscreenToggle={() => setChatFullscreen(prev => !prev)}
                                    refreshUserFiles={refreshUserFiles}
                                />
                            )}
                        </aside>
                    )}
                </div>
            </div>
            {deletePopup && typeof effectiveSelectedFile === 'number' && bloodTestFiles?.[effectiveSelectedFile] ? <TestDeletePopup
                filename={bloodTestFiles[effectiveSelectedFile]?.key}
                fileKey={bloodTestFiles[effectiveSelectedFile]?.key}
                fileKeys={bloodTestFiles[effectiveSelectedFile]?.sourceKeys}
                userFiles={userFiles}
                setUserFiles={setUserFiles}
                setSelectedFile={selectFile}
                setDeletePopup={setDeletePopup}
                patientId={patientId}
                userId={user?.userId}
            /> : ""}

            {!doctorViewMode && !readOnly && (
                <ShareModal
                    isOpen={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    bloodTestFiles={bloodTestFiles}
                    assessmentFiles={assessmentFiles}
                    preSelectedFileIndex={sharePreSelectedFile}
                    userEmail={user?.email}
                />
            )}
        </div>
    );
}

export default DesktopHome;
